import React, { useEffect, useMemo, useState } from "react";
import "./DeliveredMessages.css";
import Table from "../../../components/reusable-table/Table";
import DeliveredMessagesModal from "./modals/DeliveredMessagesModal";
import Input from "../../../components/reusable-input/Input";
import { SearchIcon } from "../../../icons/reactIcons";
import SendMessage from "./modals/SendMessage";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { getCmsMessages } from "../../../redux/slices/cmsMessageSlice";
import { fetchUserList } from "../../../redux/slices/userListSlice";
import { formatDateTime } from "../../../utils/formatDateTime";
import { fetchGroups } from "../../../redux/slices/userGroupSlice";

const columns = [
  { header: "Message", accessor: "message" },
  { header: "To", accessor: "to" },
  { header: "Created By", accessor: "createdBy" },
];

const LIMIT = 30;

const DeliveredMessages: React.FC = () => {
  const dispatch = useAppDispatch();

  const { list, studyTeam, loading, adminCount, studyTeamCount } =
    useAppSelector((state) => state.cmsMessages);

  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const adminDetail = JSON.parse(localStorage.getItem("adminDetail") || "{}");
  const userRole = adminDetail.role || "";
  const isStudyTeamUser = userRole === "study team";

  const [activeTab, setActiveTab] = useState<"admin" | "studyteam">(
    isStudyTeamUser ? "studyteam" : "admin",
  );

  const [adminPage, setAdminPage] = useState(1);
  const [studyTeamPage, setStudyTeamPage] = useState(1);

  // -----------------------------
  // FETCH DATA
  // -----------------------------
  useEffect(() => {
    dispatch(
      getCmsMessages({
        role: "admin",
        pageId: adminPage,
        limit: LIMIT,
      }),
    );
  }, [dispatch, adminPage]);

  useEffect(() => {
    dispatch(
      getCmsMessages({
        role: "study team",
        pageId: studyTeamPage,
        limit: LIMIT,
      }),
    );
  }, [dispatch, studyTeamPage]);

  useEffect(() => {
    dispatch(
      fetchUserList({
        pageId: 1,
        limit: 30,
        status: "active",
      }),
    );
    dispatch(fetchGroups());
  }, [dispatch]);

  // -----------------------------
  // TRANSFORM DATA (NEW STRUCTURE)
  // -----------------------------
  const transformData = (messages: any[]) => {
    return messages.map((msg) => ({
      message: msg.messageDetail,
      fullMessage: msg,

      to:
        msg.users?.length > 1
          ? `${msg.users[0]?.firstName} ${msg.users[0]?.lastName}, + ${
              msg.users.length - 1
            } ${msg.users.length === 2 ? "User" : "Users"}`
          : msg.users?.[0]
            ? `${msg.users[0].firstName} ${msg.users[0].lastName}`
            : "-",

      createdBy: (
        <div className="merged-cell">
          <div className="merged-label">{msg.creatorName}</div>
          <div className="merged-date">{formatDateTime(msg.createdAt)}</div>
        </div>
      ),
    }));
  };

  const adminMessages = useMemo(() => transformData(list), [list]);

  const studyTeamMessages = useMemo(
    () => transformData(studyTeam),
    [studyTeam],
  );

  // -----------------------------
  // SEARCH FILTER
  // -----------------------------
  const filteredData = useMemo(() => {
    const source = activeTab === "admin" ? adminMessages : studyTeamMessages;

    if (!searchTerm.trim()) return source;

    return source.filter((item) =>
      item.message.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, activeTab, adminMessages, studyTeamMessages]);

  const totalRecords = activeTab === "admin" ? adminCount : studyTeamCount;

  const currentPage = activeTab === "admin" ? adminPage : studyTeamPage;

  return (
    <div className="delivered-messages">
      <div className="Common-header-area">
        <h2 className="delivered-title">Delivered Messages</h2>

        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <Input
            type="text"
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            search
            startIcon={<SearchIcon fontSize={"20px"} />}
            removeBottomMargin
          />

          <button
            className="user-groups-btn"
            onClick={() => setIsSendModalOpen(true)}
          >
            Send Message
          </button>
        </div>
      </div>

      <div className="delivered-messages-headerActions">
        {!isStudyTeamUser && (
          <div className="dm-tabs">
            <button
              className={`dm-tab ${activeTab === "admin" ? "active" : ""}`}
              onClick={() => setActiveTab("admin")}
            >
              Admin <span className="tab-count">{adminCount}</span>
            </button>

            <button
              className={`dm-tab ${activeTab === "studyteam" ? "active" : ""}`}
              onClick={() => setActiveTab("studyteam")}
            >
              Study Team <span className="tab-count">{studyTeamCount}</span>
            </button>
          </div>
        )}
      </div>

      <Table
        rounded={isStudyTeamUser}
        columns={columns}
        data={filteredData}
        rowsPerPage={LIMIT}
        loading={loading}
        serverSidePagination
        totalRecords={totalRecords}
        onRowClick={(row) => setSelectedMessage(row.fullMessage)}
        onPageChange={(newPage) => {
          if (activeTab === "admin") {
            setAdminPage(newPage);
          } else {
            setStudyTeamPage(newPage);
          }
        }}
      />

      <DeliveredMessagesModal
        isOpen={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        messageData={selectedMessage}
      />

      <SendMessage
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        onSuccess={() => {
          setAdminPage(1);
          setStudyTeamPage(1);
          dispatch(
            getCmsMessages({
              role: "admin",
              pageId: 1,
              limit: LIMIT,
            }),
          );
          dispatch(
            getCmsMessages({
              role: "study team",
              pageId: 1,
              limit: LIMIT,
            }),
          );
        }}
      />
    </div>
  );
};

export default DeliveredMessages;
